/*  iceberg-ui  */
import { Flex } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { CellContext } from '@tanstack/react-table';
import {
    DateFormatCell,
    StringCell
} from 'application/Shared/Components/Cells';
import { KebabMenu } from 'application/Shared/Components/Menu/KebabMenu';
import {
    Header,
    ManagedTableV2ContextProvider,
    ManagedTableV2UI
} from 'application/Shared/Components/Table';

/* Shared  */
import { Route } from 'application/Shared/Helpers';
import { ClientLink } from 'application/Shared/types/clientLink';

/*  Helpers */
import { Tenant } from 'application/Shared/types/tenant';

/*  React  */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import {
    ClientsTableContextProvider,
    useClientsTableContext
} from './ClientsTableContext';
import { CustomCard } from './Shared/CustomCard';

const ActionsCell = ({ row }: CellContext<ClientLink, undefined>) => {
    const { partnerTenant } = useClientsTableContext();
    const baseRoute = Route.buildBaseAdminRoute({
        clientId: row.original.tenant?.id as string,
        partnerId: partnerTenant?.id as string
    });

    return (
        <Flex justifyContent="flex-end">
            <KebabMenu
                options={[
                    {
                        key: 'view',
                        label: 'View Client',
                        link: {
                            to: baseRoute
                        },
                        value: 'view'
                    }
                ]}
                onClick={() => {}}
                data-testid="clientsTableActions"
            />
        </Flex>
    );
};

const LinkCell = (props: CellContext<ClientLink, string>) => {
    const value = props.cell.getValue();
    const { tenant } = props.row.original;

    return <Link to={`/globaladmin/tenant/${tenant?.id}`}>{value}</Link>;
};

const columns = [
    {
        accessorKey: 'tenant.name',
        cell: LinkCell,
        enableColumnFilter: false,
        enableSorting: true,
        header: Header,
        id: 'name',
        name: 'Common.name',
        size: 160
    },
    {
        accessorKey: 'tenant.id',
        cell: StringCell,
        enableColumnFilter: false,
        enableSorting: false,
        header: Header,
        id: 'id',
        name: 'Common.id',
        size: 160
    },
    {
        accessorKey: 'tenant.dateCreated',
        cell: DateFormatCell,
        enableColumnFilter: false,
        header: Header,
        id: 'dateCreated',
        name: 'Common.dateCreated',
        size: 160
    },
    {
        cell: ActionsCell,
        enableColumnFilter: false,
        enableSorting: false,
        header: Header,
        id: 'actions',
        meta: {
            align: 'right',
            header: { justifyContent: 'flex-end' }
        },
        name: 'Common.actions',
        size: 20,
        testId: 'actions'
    }
];

export const ClientsTable = ({ tenant }: { tenant: Tenant }) => {
    const baseParameters = useMemo(
        () => ({
            global: true,
            includeTenant: true,
            platformTenantId: tenant.id,
            region: tenant.region,
            size: -1
        }),
        [tenant.id, tenant.region]
    );

    return (
        <ManagedTableV2ContextProvider
            columns={columns}
            rowsApi="/clientLink"
            baseParameters={baseParameters}
            getRowId="id"
            ignoreSearchParams
            mode="client"
            rowsApiPreload
            rowsPerPage={10}
            searchEnabled
            sortKey="name"
            sortDirection="ASC"
            tableName="globalAdminClientsTable"
        >
            <ClientsTableContextProvider partnerTenant={tenant}>
                <CustomCard noPadding>
                    <ManagedTableV2UI
                        titleDef={{
                            titleKey:
                                'GlobalAdmin.Tenant.Details.ClientsTable.title'
                        }}
                        renderFooter
                    />
                </CustomCard>
            </ClientsTableContextProvider>
        </ManagedTableV2ContextProvider>
    );
};
